import {DataSourceConfig} from './data-source-config.model';
import {IntegrationMode} from './integration-mode.model';
import {IntegrationTask} from './integration-task.model';
import {OrganizationsMapping} from './organizationsMapping.model';
import {ProcessConfig} from './process-config.model';
import {IntegrationState} from './integration-state.model';
import {DictionaryModel} from '@app/views/admin/application-settings/models/dictionary.model';
import {RequestedSchoolRoles} from '@app/core/model/integration-models';
import {DataSourceType} from '@app/views/admin/application-settings/models/data-source-type.model';

export class Integration {
    activatedRolesForSchools: RequestedSchoolRoles;
    failedToGetIntegration: boolean;
    appAssignmentMode: SsoAppAccessModes;

    constructor(
        public integrationId: string = '',
        public title: string = '',
        public source: DataSourceConfig = null,
        public target: DataSourceConfig = null,
        public mode: IntegrationMode = null,
        public hasMultipleOrgs: boolean = false,
        public tasks: IntegrationTask[] = <IntegrationTask[]>[],
        public organizationsMappingModel: OrganizationsMapping = null,
        public processConfigModel: ProcessConfig[] = <ProcessConfig[]>[],
        public state: IntegrationState = null,
        public expirationTs: Date = null,
        public dictionaries: DictionaryModel[] = [],
        public applicationId: string = '',
        public launchTimestamp: Date = null
    ) { }

    static isIntegrationSyncAllowed(state: IntegrationState): boolean {
        return [
            IntegrationState.SYNCED,
            IntegrationState.SUSPENDED,
            IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_OTHER,
            IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_ACCESS,
            IntegrationState.SIGNIFICANT_CHANGES_DETECTED_IN_ACCESS_AND_SYNC
        ].indexOf(state) > -1;
    }

    static getEduToneTypes(integration: Integration): string[] {
        if (!integration) {
            return [];
        }
        const eduToneDS = (integration.source.dataSourceType === DataSourceType.EduTone) ? 'source' : 'target';
        return integration.tasks.map((task: IntegrationTask) => task[eduToneDS + 'Type']);
    }
}

export const ROSTER_STUDENT_ENROLLMENT = 'RosterStudentEnrollment';
export const SCHOOL = 'School';

export const INTEGRATION_TASKS_ORDER = {
    'Districts': 0,
    'School': 1,
    'Student': 2,
    'Contact': 3,
    'Staff': 4,
    'Course': 5,
    'Roster': 6,
    'RosterStudentEnrollment': 7,
    'Group': 8,
    'Calendar': 9,
    'SchoolAdmin': 10,
    'LineItem': 11,
    'Result': 12,
    'AssessmentLineItem': 13,
    'AssessmentResult': 14,
    'ScoreScale': 15,
    'Category': 16,
    'Resource': 17
};

export const INTEGRATION_TASKS_EDUTONE_TITLE_MAP = {
    'Staff': 'Teacher',
    'Roster': 'Class',
    'RosterStudentEnrollment': 'Enrollment',
    'SchoolAdmin': 'Admin',
    'LineItem': 'Line Item',
    'AssessmentLineItem': 'Assessment Line Item',
    'AssessmentResult': 'Assessment Result',
    'ScoreScale': 'Score Scale'
};

export const INTEGRATION_TASKS_EDUTONE_TITLE_PLURAL_MAP = {
    'Districts': 'Districts',
    'School': 'Schools',
    'Student': 'Students',
    'Contact': 'Contacts',
    'Staff': 'Teachers',
    'Course': 'Courses',
    'Roster': 'Classes',
    'RosterStudentEnrollment': 'Enrollments',
    'Calendar': 'Calendars',
    'Group': 'Groups',
    'SchoolAdmin': 'Admins',
    'LineItem': 'Line Items',
    'Result': 'Results',
    'AssessmentLineItem': 'Assessment Line Items',
    'AssessmentResult': 'Assessment Results',
    'ScoreScale': 'Score Scales',
    'Category': 'Categories',
    'Resource': 'Resources',
    'User': 'Users'
};

export const INTEGRATION_TASKS_FA_CLASS = {
    'District': 'university',
    'School': 'school',
    'Student': 'user-graduate',
    'Contact': 'user-friends',
    'Staff': 'chalkboard-teacher',
    'Course': 'chalkboard',
    'Roster': 'users',
    'RosterStudentEnrollment': 'id-card',
    'Calendar': 'calendar-alt',
    'Group': 'users',
    'SchoolAdmin': 'user-tie',
    'LineItem': 'clipboard-list',
    'Result': 'poll',
    'AssessmentLineItem': 'clipboard-list',
    'AssessmentResult': 'poll',
    'ScoreScale': 'tachometer-alt',
    'Category': 'th-large',
    'Resource': 'folder',
    'User': 'user'
};

export const INTEGRATION_TASKS_ORDER_FOR_FILTER_RULES = {
    'Calendar': 1,
    'Course': 2,
    'Section': 3,
    'Staff': 4,
    'Student': 5,
    'Admin': 6,
    'Districts': 7,
    'School': 8,
    'Contact': 9,
    'Enrollment': 10,
    'Group': 11,
    'LineItem': 12,
    'Result': 13,
    'AssessmentLineItem': 14,
    'AssessmentResult': 15,
    'ScoreScale': 16,
    'Category': 17,
    'Resource': 18
};

/* Change ClientPIIData if new attributes were added */
export const DATA_MASKING_ENABLED_METADATA_ATTRIBUTES = [
    'givenName',
    'familyName',
    'middleName',
    'emailAddress',
    'username',
    'birthDate',
    'number'
    // 'phoneNumber',
    // 'otherPhoneNumbers'
];

export enum INTEGRATION_TASK_TYPES {
    'rostering' = 'rostering',
    'gradebook' = 'gradebook',
    'resource' = 'resource'
}

export const INTEGRATION_TASKS_BY_TYPES = {
    [INTEGRATION_TASK_TYPES.rostering]: [
        'Districts', 'School', 'Student', 'Contact', 'Staff', 'Course', 'Roster', 'RosterStudentEnrollment', 'Group', 'Calendar', 'SchoolAdmin'
    ],
    [INTEGRATION_TASK_TYPES.gradebook]: [
        'LineItem', 'Result', 'AssessmentLineItem', 'AssessmentResult', 'ScoreScale', 'Category'
    ],
    [INTEGRATION_TASK_TYPES.resource]: [
        'Resource'
    ],
};

export const PERSON_ENTITIES_LIST = ['Student', 'Contact', 'Staff', 'SchoolAdmin'];

export enum PAGES_WHERE_INTEGRATION_TASK_ARE_SHOWN {
    'attributesMapping' = 'attributesMapping',
    'dataRequirements' = 'dataRequirements',
    'filterRules' = 'filterRules',
    'domainOfUniqueness' = 'domainOfUniqueness',
    'masterFields' = 'masterFields'
}

export enum SsoAppAccessModes {
    APP_ASSIGNMNETS_ACCESS = 'APP_ASSIGNMNETS_ACCESS',
    APP_ASSIGNMNETS_ACCESS_AND_SSO_FILTERS = 'APP_ASSIGNMNETS_ACCESS_AND_SSO_FILTERS',
    APP_ASSIGNMENTS_IN_APP_STORE = 'APP_ASSIGNMENTS_IN_APP_STORE'
}
